function prepareAveyQuickBuy(root) {
  root.querySelectorAll('[data-avey-quick-buy]').forEach((trigger) => { trigger.hidden = false; });
}

function handleAveyQuickBuy(event) {
  const trigger = event.target.closest('[data-avey-quick-buy]');
  if (!trigger) return false;
  event.preventDefault();
  const dialog = document.getElementById(trigger.getAttribute('aria-controls'));
  const controller = dialog?.closest('avey-menu-quick-buy');
  if (controller?.open && window.AveyOverlayManager) controller.open(trigger);
  else window.location.assign(trigger.dataset.productUrl);
  return true;
}

class AveyMenuQuickBuy extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.copy = JSON.parse(this.querySelector('[data-quick-buy-copy]').textContent);
    this.generation = 0;
    this.dialog.addEventListener('close', this.onClose);
  }

  disconnectedCallback() {
    this.generation++;
    this.dialog?.removeEventListener('close', this.onClose);
    this.controller?.destroy();
  }

  onClose = () => {
    this.generation++;
    this.controller?.cancelLoad();
  };

  async open(trigger) {
    if (this.controller?.busy) return;
    const generation = ++this.generation;
    const status = this.querySelector('[data-quick-buy-status]');
    const error = this.querySelector('[data-quick-buy-error]');
    this.querySelector('[data-quick-buy-form]').hidden = true;
    this.querySelector('[data-quick-buy-preference]').hidden = true;
    status.textContent = this.copy.loading;
    status.hidden = false;
    error.hidden = true;
    this.querySelector('.avey-overlay__title').textContent = trigger.dataset.productTitle;
    this.querySelector('[data-quick-buy-product-link]').href = trigger.dataset.productUrl;
    const popover = trigger.closest('[popover]');
    const opener = popover?.parentElement.querySelector('.avey-header__nav-trigger') || trigger;
    if (popover?.matches(':popover-open')) popover.hidePopover();
    await window.AveyOverlayManager.open(this.dialog, opener, { stack: Boolean(trigger.closest('dialog[open]')) });
    try {
      const { AveyQuickBuy } = await import(this.dataset.module);
      if (generation !== this.generation || !this.dialog.open) return;
      this.controller ||= new AveyQuickBuy(this);
      await this.controller.load(trigger, generation, opener);
    } catch (_error) {
      if (generation !== this.generation || !this.dialog.open) return;
      status.hidden = true;
      error.textContent = this.copy.loadError;
      error.hidden = false;
    }
  }
}

if (!customElements.get('avey-menu-quick-buy')) customElements.define('avey-menu-quick-buy', AveyMenuQuickBuy);

class AveyMenuProductRoll extends HTMLElement {
  static storageKey = 'avey-menu-product-roll-open-count';

  static openingInterval = 4;

  constructor() {
    super();
    this.video = null;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.handleOverlayOpen = this.handleOverlayOpen.bind(this);
    this.handleOverlayClose = this.handleOverlayClose.bind(this);
    this.handleVideoEnded = this.handleVideoEnded.bind(this);
    this.fallbackOpenCount = 0;
  }

  connectedCallback() {
    this.video = this.querySelector('video[data-src]');
    if (!this.video) return;

    document.addEventListener('avey:overlay:open', this.handleOverlayOpen);
    document.addEventListener('avey:overlay:close', this.handleOverlayClose);
    this.video.addEventListener('ended', this.handleVideoEnded);
  }

  disconnectedCallback() {
    document.removeEventListener('avey:overlay:open', this.handleOverlayOpen);
    document.removeEventListener('avey:overlay:close', this.handleOverlayClose);
    this.video?.removeEventListener('ended', this.handleVideoEnded);
  }

  handleOverlayOpen(event) {
    if (!event.detail?.overlay?.contains(this)) return;

    if (this.reducedMotion.matches) {
      this.classList.remove('is-selected-opening');
      this.hidden = true;
      return;
    }

    const openCount = this.getNextOpenCount();
    const shouldPlay = (openCount - 1) % AveyMenuProductRoll.openingInterval === 0;

    if (!shouldPlay) {
      this.classList.remove('is-selected-opening');
      this.hidden = true;
      return;
    }

    this.classList.add('is-selected-opening');
    this.hidden = false;

    if (!this.video.src) {
      this.video.src = this.video.dataset.src;
      this.video.load();
    }

    this.video.currentTime = 0;
    this.video.play().catch(() => {});
  }

  handleOverlayClose(event) {
    if (!event.detail?.overlay?.contains(this)) return;
    this.video.pause();
    this.video.currentTime = 0;
    this.classList.remove('is-selected-opening');
    this.hidden = true;
  }

  handleVideoEnded() {
    this.hidden = true;
  }

  getNextOpenCount() {
    try {
      const storedCount = Number.parseInt(
        window.localStorage.getItem(AveyMenuProductRoll.storageKey) || '0',
        10,
      );
      const nextCount = Number.isFinite(storedCount) ? storedCount + 1 : 1;
      window.localStorage.setItem(AveyMenuProductRoll.storageKey, String(nextCount));
      return nextCount;
    } catch (_error) {
      this.fallbackOpenCount += 1;
      return this.fallbackOpenCount;
    }
  }
}

if (!customElements.get('avey-menu-product-roll')) {
  customElements.define('avey-menu-product-roll', AveyMenuProductRoll);
}

class AveyDrawerNavigation extends HTMLElement {
  connectedCallback() {
    prepareAveyQuickBuy(this);
    this.animations = new Map();
    this.movements = new Map();
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.content = this.parentElement;
    this.contact = this.content.querySelector('.avey-menu-drawer__contact');
    this.contactAnimation = null;
    this.contactTarget = null;
    this.dialog = this.closest('dialog');
    this.addEventListener('click', this.handleClick);
    this.addEventListener('toggle', this.handleToggle, true);
    this.dialog?.addEventListener('close', this.handleDialogClose);
    document.addEventListener('avey:overlay:open', this.handleOverlayOpen);
    this.reducedMotion.addEventListener('change', this.handleMotionChange);
    this.prepareImages();
    this.updateContact(false);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('toggle', this.handleToggle, true);
    this.dialog?.removeEventListener('close', this.handleDialogClose);
    document.removeEventListener('avey:overlay:open', this.handleOverlayOpen);
    this.reducedMotion.removeEventListener('change', this.handleMotionChange);
    this.animations.forEach((state, group) => this.finish(group, state));
    this.clearMovements();
    this.contactAnimation?.cancel();
    this.contactAnimation = null;
    delete this.content.dataset.contactLayout;
  }

  handleOverlayOpen = (event) => {
    if (event.detail?.overlay?.contains(this)) {
      this.prepareImages();
      this.updateContact(false);
    }
  };

  handleToggle = () => {
    this.prepareImages();
    this.updateContact();
  };

  handleDialogClose = () => this.updateContact(false);

  async updateContact(animate = true) {
    if (!this.contact) return;
    const content = this.content;
    const canCompact = this.contact.hasAttribute('data-contact-compact-enabled');
    const target = canCompact && this.querySelector('.avey-menu-drawer__group[open]:not([data-closing])') ? 'compact' : 'stacked';
    if (animate && target === this.contactTarget && (this.contactAnimation || content.dataset.contactLayout === target)) return;
    this.contactTarget = target;
    const opacity = this.contactAnimation ? Number.parseFloat(getComputedStyle(this.contact).opacity) : 1;
    this.contactAnimation?.cancel();
    this.contactAnimation = null;
    if (!animate || this.reducedMotion.matches || !this.dialog?.open || typeof this.contact.animate !== 'function') {
      content.dataset.contactLayout = target;
      return;
    }
    if (content.dataset.contactLayout === target && opacity >= 1) return;
    const styles = getComputedStyle(this.contact);
    const duration = styles.getPropertyValue('--motion-fast').trim();
    const timing = {
      duration: Number.parseFloat(duration) * (duration.endsWith('ms') ? 1 : 1000) || 160,
      easing: 'linear',
      fill: 'both',
    };
    let fadeOut;
    try {
      if (content.dataset.contactLayout !== target) {
        fadeOut = this.contact.animate([{ opacity }, { opacity: 0 }], timing);
        this.contactAnimation = fadeOut;
        await fadeOut.finished;
        if (this.contactAnimation !== fadeOut) return;
        content.dataset.contactLayout = target;
      }
      const fadeIn = this.contact.animate([{ opacity: fadeOut ? 0 : opacity }, { opacity: 1 }], timing);
      this.contactAnimation = fadeIn;
      fadeOut?.cancel();
      await fadeIn.finished;
      if (this.contactAnimation !== fadeIn) return;
      this.contactAnimation = null;
      fadeIn.cancel();
    } catch (_error) {}
  }

  prepareImages = () => {
    if (!window.matchMedia('(max-width: 1023px)').matches || !this.closest('dialog')?.open) return;
    const group = this.querySelector('[data-avey-menu-products][open]:not([data-closing])');
    group?.querySelectorAll('img[data-avey-menu-src]').forEach((image) => {
      image.srcset = image.dataset.aveyMenuSrcset;
      image.src = image.dataset.aveyMenuSrc;
      delete image.dataset.aveyMenuSrcset;
      delete image.dataset.aveyMenuSrc;
    });
  };

  handleClick = (event) => {
    if (handleAveyQuickBuy(event)) return;
    const summary = event.target.closest('summary');
    const group = summary?.parentElement;
    const sublist = group?.querySelector('.avey-menu-drawer__sublist');
    if (!group?.matches('.avey-menu-drawer__group') || !sublist || !this.contains(group)) return;
    if (event.button !== 0 || this.reducedMotion.matches || typeof sublist.animate !== 'function') return;

    event.preventDefault();
    const previous = this.animations.get(group);
    const opening = previous ? !previous.opening : !group.open;
    const opacity = group.open ? Number.parseFloat(getComputedStyle(sublist).opacity) : 0;
    const elements = [
      ...this.querySelectorAll('.avey-menu-drawer__item'),
      ...[...this.parentElement.children].filter((element) => element !== this && !element.hidden),
    ];
    // Read the visible positions before cancelling an interrupted movement.
    const positions = elements.map((element) => ({ element, top: element.getBoundingClientRect().top }));
    const styles = getComputedStyle(this);
    const duration = styles.getPropertyValue('--motion-fast').trim();
    const timing = {
      duration: Number.parseFloat(duration) * (duration.endsWith('ms') ? 1 : 1000) || 120,
      easing: styles.getPropertyValue('--ease-standard').trim() || 'ease-out',
      fill: 'both',
    };
    previous?.animation.cancel();
    this.clearMovements();

    group.open = true;
    group.toggleAttribute('data-closing', !opening);
    group.setAttribute('data-animating', '');
    sublist.inert = !opening;
    this.updateContact();
    // Batch the final layout reads; only transforms and opacity animate afterwards.
    const offsets = positions.map(({ element, top }) => ({
      element,
      offset: top - element.getBoundingClientRect().top,
    }));
    offsets.forEach(({ element, offset }) => {
      if (Math.abs(offset) < 0.5) return;
      element.setAttribute('data-menu-moving', '');
      const movement = element.animate(
        [{ transform: `translateY(${offset}px)` }, { transform: 'translateY(0)' }],
        timing,
      );
      this.movements.set(element, movement);
      movement.finished.then(() => {
        if (this.movements.get(element) !== movement) return;
        element.removeAttribute('data-menu-moving');
        this.movements.delete(element);
        movement.cancel();
      }, () => {});
    });
    const animation = sublist.animate([{ opacity }, { opacity: opening ? 1 : 0 }], timing);
    const state = { animation, opening, sublist };
    this.animations.set(group, state);
    animation.finished.then(() => this.finish(group, state), () => {});
  };

  handleMotionChange = () => {
    if (this.reducedMotion.matches) {
      this.animations.forEach((state, group) => this.finish(group, state));
      this.clearMovements();
      this.updateContact(false);
    }
  };

  clearMovements() {
    this.movements.forEach((animation, element) => {
      animation.cancel();
      element.removeAttribute('data-menu-moving');
    });
    this.movements.clear();
  }

  finish(group, state) {
    if (this.animations.get(group) !== state) return;
    group.open = state.opening;
    state.sublist.inert = false;
    group.removeAttribute('data-animating');
    group.removeAttribute('data-closing');
    this.animations.delete(group);
    state.animation.cancel();
  }
}

if (!customElements.get('avey-drawer-navigation')) {
  customElements.define('avey-drawer-navigation', AveyDrawerNavigation);
}

class AveyDesktopNavigation extends HTMLElement {
  static closeDelay = 180;

  constructor() {
    super();
    this.items = [];
    this.overlay = null;
    this.pointerQuery = window.matchMedia('(min-width: 1024px) and (hover: hover) and (pointer: fine)');
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.handlePointerModeChange = this.handlePointerModeChange.bind(this);
  }

  connectedCallback() {
    if (this.dataset.hoverInitialized === 'true' || !('showPopover' in HTMLElement.prototype)) return;
    this.dataset.hoverInitialized = 'true';
    prepareAveyQuickBuy(this);
    this.addEventListener('click', handleAveyQuickBuy);
    this.overlay = this.closest('.avey-header-shell')?.parentElement?.querySelector('[data-avey-navigation-overlay]');

    this.querySelectorAll('.avey-header__nav-group').forEach((group) => {
      this.registerItem(group, group.querySelector('.avey-header__nav-trigger'), group.querySelector('.avey-header__nav-popover'));
    });

    this.items.forEach((item) => {
      if (item.popover.matches(':popover-open')) this.prepareImages(item);
    });
    this.syncOverlay();
    this.overlay?.addEventListener('click', this.handleOverlayClick);
    this.pointerQuery.addEventListener('change', this.handlePointerModeChange);
  }

  registerItem(group, trigger, popover) {
    if (!('showPopover' in HTMLElement.prototype)) return null;
    const existing = this.items.find((item) => item.popover === popover);
    if (existing) return existing;
    if (!(trigger instanceof HTMLButtonElement) || !(popover instanceof HTMLElement)) return null;

    const item = {
      group,
      trigger,
      popover,
      pinned: false,
      closeTimer: null,
    };
    item.handleTriggerEnter = () => this.openOnHover(item);
    item.handleTriggerLeave = () => this.scheduleClose(item);
    item.handlePopoverEnter = () => this.clearCloseTimer(item);
    item.handlePopoverLeave = () => this.scheduleClose(item);
    item.handleTriggerClick = (event) => {
      event.preventDefault();
      this.items.forEach((other) => this.clearCloseTimer(other));
      if (item.pinned && popover.matches(':popover-open')) {
        popover.hidePopover();
      } else {
        // The first click keeps a hover-open submenu available after the pointer leaves.
        item.pinned = true;
        if (!popover.matches(':popover-open')) popover.showPopover();
      }
    };
    item.handleBeforeToggle = (event) => {
      if (event.newState === 'open') this.prepareImages(item);
      if (event.newState !== 'closed') return;
      item.pinned = false;
      this.clearCloseTimer(item);
    };
    item.handleToggle = () => {
      trigger.setAttribute('aria-expanded', String(popover.matches(':popover-open')));
      this.syncOverlay();
    };

    trigger.setAttribute('aria-expanded', String(popover.matches(':popover-open')));
    trigger.addEventListener('pointerenter', item.handleTriggerEnter);
    trigger.addEventListener('pointerleave', item.handleTriggerLeave);
    trigger.addEventListener('click', item.handleTriggerClick);
    popover.addEventListener('pointerenter', item.handlePopoverEnter);
    popover.addEventListener('pointerleave', item.handlePopoverLeave);
    popover.addEventListener('beforetoggle', item.handleBeforeToggle);
    popover.addEventListener('toggle', item.handleToggle);
    this.items.push(item);
    return item;
  }

  disconnectedCallback() {
    this.removeEventListener('click', handleAveyQuickBuy);
    this.overlay?.removeEventListener('click', this.handleOverlayClick);
    this.overlay?.classList.remove('is-visible');
    this.pointerQuery.removeEventListener('change', this.handlePointerModeChange);
    this.items.slice().forEach((item) => this.unregisterItem(item));
    this.items = [];
    this.overlay = null;
    delete this.dataset.hoverInitialized;
  }

  unregisterItem(item) {
    if (!item) return;
    this.clearCloseTimer(item);
    item.trigger.removeEventListener('pointerenter', item.handleTriggerEnter);
    item.trigger.removeEventListener('pointerleave', item.handleTriggerLeave);
    item.trigger.removeEventListener('click', item.handleTriggerClick);
    item.popover.removeEventListener('pointerenter', item.handlePopoverEnter);
    item.popover.removeEventListener('pointerleave', item.handlePopoverLeave);
    item.popover.removeEventListener('beforetoggle', item.handleBeforeToggle);
    item.popover.removeEventListener('toggle', item.handleToggle);
    this.items = this.items.filter((entry) => entry !== item);
  }

  openOnHover(item) {
    this.clearCloseTimer(item);
    if (!this.pointerQuery.matches || this.items.some((other) => other.pinned) || !item.trigger.matches(':hover')) return;
    if (!item.popover.matches(':popover-open')) item.popover.showPopover();
  }

  prepareImages(item) {
    if (!window.matchMedia('(min-width: 1024px)').matches) return;
    item.popover.querySelectorAll('img[data-avey-menu-src]').forEach((image) => {
      image.srcset = image.dataset.aveyMenuSrcset;
      image.src = image.dataset.aveyMenuSrc;
      delete image.dataset.aveyMenuSrcset;
      delete image.dataset.aveyMenuSrc;
    });
  }

  scheduleClose(item) {
    this.clearCloseTimer(item);
    if (!this.pointerQuery.matches || item.pinned) return;

    item.closeTimer = window.setTimeout(() => {
      item.closeTimer = null;
      const focusInside = item.group.contains(document.activeElement);
      if (item.pinned || item.trigger.matches(':hover') || item.popover.matches(':hover') || focusInside) return;
      if (item.popover.matches(':popover-open')) item.popover.hidePopover();
    }, AveyDesktopNavigation.closeDelay);
  }

  clearCloseTimer(item) {
    window.clearTimeout(item.closeTimer);
    item.closeTimer = null;
  }

  handleOverlayClick() {
    this.items.forEach((item) => {
      this.clearCloseTimer(item);
      if (item.popover.matches(':popover-open')) item.popover.hidePopover();
    });
  }

  syncOverlay() {
    const hasOpenPopover = this.items.some((item) => item.popover.matches(':popover-open'));
    this.overlay?.classList.toggle('is-visible', hasOpenPopover && this.pointerQuery.matches);
  }

  handlePointerModeChange() {
    if (this.pointerQuery.matches) {
      this.syncOverlay();
      return;
    }
    this.items.forEach((item) => {
      this.clearCloseTimer(item);
      if (item.popover.matches(':popover-open')) item.popover.hidePopover();
    });
    this.overlay?.classList.remove('is-visible');
  }
}

if (!customElements.get('avey-desktop-navigation')) {
  customElements.define('avey-desktop-navigation', AveyDesktopNavigation);
}

class AveyAccountNavigation extends HTMLElement {
  connectedCallback() {
    this.menu = this.querySelector('[popover]');
    const header = this.closest('.avey-header-shell');
    this.navigation = header?.querySelector?.('avey-desktop-navigation');
    this.navigationItem = this.navigation?.registerItem(this, this.querySelector('.avey-header__customer-account-trigger'), this.menu);
    this.overlay = header?.parentElement?.querySelector('[data-avey-navigation-overlay]');
    this.mobileQuery = window.matchMedia('(max-width: 1023px)');
    // Native light dismissal can hide the veil before the touch's click is dispatched.
    // Retain ownership of that gesture even if the browser has already closed the popover.
    this.onOutsidePointerDown = (event) => {
      this.blockOutsideClick = this.menu.matches(':popover-open') && !header?.contains(event.target);
      if (this.blockOutsideClick) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    this.onOutsideClick = (event) => {
      if (!this.blockOutsideClick) return;
      this.blockOutsideClick = false;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.menu.matches(':popover-open')) this.menu.hidePopover();
    };
    this.onPointerCancel = () => { this.blockOutsideClick = false; };
    this.onPageScroll = () => {
      if (!this.mobileQuery.matches || !this.menu.matches(':popover-open')) return;
      if (Math.abs(window.scrollY - this.openScrollY) < 1) return;
      window.removeEventListener('scroll', this.onPageScroll);
      this.menu.hidePopover();
    };
    this.onToggle = () => {
      const open = this.menu.matches(':popover-open');
      this.overlay?.classList.toggle('is-account-visible', open);
      window.removeEventListener('scroll', this.onPageScroll);
      if (open) {
        this.openScrollY = window.scrollY;
        window.addEventListener('scroll', this.onPageScroll, { passive: true });
      }
    };
    this.onOverlayClick = () => { if (this.menu.matches(':popover-open')) this.menu.hidePopover(); };
    this.onOpen = (event) => {
      if (event.newState !== 'open') return;
      const manager = window.AveyOverlayManager;
      if (manager?.activeOverlay?.hasAttribute('data-avey-overlay-keep-header')) {
        manager.close(manager.activeOverlay, { restoreFocus: false });
      }
    };
    this.onSelect = (event) => {
      if (event.target.closest('a') && this.menu.matches(':popover-open')) this.menu.hidePopover();
    };
    this.menu.addEventListener('toggle', this.onToggle);
    this.overlay?.addEventListener('click', this.onOverlayClick);
    this.menu.addEventListener('beforetoggle', this.onOpen);
    this.menu.addEventListener('click', this.onSelect);
    document.addEventListener('pointerdown', this.onOutsidePointerDown, true);
    document.addEventListener('click', this.onOutsideClick, true);
    document.addEventListener('pointercancel', this.onPointerCancel, true);
  }

  disconnectedCallback() {
    this.navigation?.unregisterItem(this.navigationItem);
    this.navigation = null;
    this.navigationItem = null;
    window.removeEventListener('scroll', this.onPageScroll);
    this.overlay?.classList.remove('is-account-visible');
    this.overlay?.removeEventListener('click', this.onOverlayClick);
    this.menu.removeEventListener('toggle', this.onToggle);
    this.menu.removeEventListener('beforetoggle', this.onOpen);
    this.menu.removeEventListener('click', this.onSelect);
    document.removeEventListener('pointerdown', this.onOutsidePointerDown, true);
    document.removeEventListener('click', this.onOutsideClick, true);
    document.removeEventListener('pointercancel', this.onPointerCancel, true);
  }
}

if (!customElements.get('avey-account-navigation')) {
  customElements.define('avey-account-navigation', AveyAccountNavigation);
}

class AveySearchLoader extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.host = this.querySelector('avey-instant-search');
    this.scroller = this.dialog.querySelector('[data-avey-overlay-scroll]');
    this.surface = this.dialog.querySelector('.avey-overlay__surface');
    this.scrollMore = this.host.querySelector('[data-search-scroll-more]');
    this.scroller.tabIndex = -1;
    this.scrollMore?.addEventListener('click', this.onScrollMore);
    this.surface.removeAttribute('data-avey-overlay-initial-focus');
    this.dialog.addEventListener('keydown', this.onKeydown);
    this.dialog.addEventListener('focusin', this.queueResultsLimit);
    this.dialog.addEventListener('close', this.stopResultsLimit);
    this.dialog.addEventListener('transitionend', this.onSurfaceMotion);
    document.addEventListener('avey:overlay:open', this.onOpen);
    if (document.readyState !== 'complete') document.addEventListener('DOMContentLoaded', this.openLinkedGuide, { once: true });
    else this.openLinkedGuide();
  }

  disconnectedCallback() {
    this.stopResultsLimit();
    this.scrollMore?.removeEventListener('click', this.onScrollMore);
    this.dialog?.removeEventListener('keydown', this.onKeydown);
    this.dialog?.removeEventListener('focusin', this.queueResultsLimit);
    this.dialog?.removeEventListener('close', this.stopResultsLimit);
    this.dialog?.removeEventListener('transitionend', this.onSurfaceMotion);
    document.removeEventListener('avey:overlay:open', this.onOpen);
    document.removeEventListener('DOMContentLoaded', this.openLinkedGuide);
  }

  onOpen = async (event) => {
    if (event.detail?.overlay !== this.dialog) {
      this.clearResultsLimit();
      return;
    }
    this.stopResultsLimit();
    this.resetReadMore();
    this.scroller.addEventListener('scroll', this.onResultsScroll, { passive: true });
    this.scroller.addEventListener('scrollend', this.finishReadMoreScroll);
    if (typeof ResizeObserver === 'function') {
      this.scrollObserver = new ResizeObserver(this.queueScrollCues);
      this.scrollObserver.observe(this.scroller);
      for (const panel of this.scroller.children) this.scrollObserver.observe(panel);
    }
    this.queueScrollCues();
    this.viewport = window.visualViewport;
    this.viewport?.addEventListener('resize', this.queueResultsLimit);
    this.viewport?.addEventListener('scroll', this.queueResultsLimit);
    this.host.dataset.openMode = this.selectMode(window.AveyOverlayManager?.returnFocusElement?.dataset.searchMode, true);
    this.queueResultsLimit();
    try {
      await (this.module ||= import(this.dataset.module));
    } catch (_error) {
      this.module = null;
      if (this.isConnected) this.host.querySelector('[data-search-status]').textContent = this.host.dataset.error;
    }
  };

  queueResultsLimit = () => {
    if (!this.viewport || !this.dialog.open || this.dialog.dataset.state === 'closing' || this.resultsFrame) return;
    this.resultsFrame = requestAnimationFrame(() => {
      this.resultsFrame = null;
      this.limitResults();
    });
  };

  onSurfaceMotion = (event) => {
    if (event.target === this.surface && event.propertyName === 'transform') this.queueResultsLimit();
  };

  limitResults() {
    if (!this.dialog.open || !this.viewport || this.viewport.scale !== 1 ||
      !window.matchMedia('(max-width: 767px)').matches ||
      (window.AveyOverlayManager?.activeOverlay && window.AveyOverlayManager.activeOverlay !== this.dialog)) {
      this.clearResultsLimit();
      return;
    }
    const visibleBottom = this.viewport.offsetTop + this.viewport.height;
    if (Math.max(window.innerHeight, document.documentElement.clientHeight) - visibleBottom < 140) {
      this.clearResultsLimit();
      return;
    }
    // The header, field and submit button never move with the keyboard.
    // Only the results' scrollable area ends above the visible keyboard.
    const available = Math.max(0, visibleBottom - this.scroller.getBoundingClientRect().top - 12);
    this.scroller.style.setProperty('--avey-search-results-height', `${available}px`);
  }

  clearResultsLimit() {
    this.scroller?.style.removeProperty('--avey-search-results-height');
  }

  queueScrollCues = () => {
    if (!this.dialog.open || this.dialog.dataset.state === 'closing' || this.cuesFrame) return;
    this.cuesFrame = requestAnimationFrame(() => {
      this.cuesFrame = null;
      const { scrollTop, scrollHeight, clientHeight } = this.scroller;
      this.scroller.toggleAttribute('data-search-scroll-start', scrollTop > 1);
      const maximum = Math.max(0, scrollHeight - clientHeight);
      const more = maximum - scrollTop > 1;
      if (maximum > 1 && !more) this.readMoreCompleted = true;
      this.scroller.toggleAttribute('data-search-scroll-end', more);
      const keyboard = this.viewport && this.viewport.scale === 1 && window.matchMedia('(max-width: 767px)').matches &&
        Math.max(window.innerHeight, document.documentElement.clientHeight) - this.viewport.offsetTop - this.viewport.height >= 140;
      if (this.scrollMore) this.scrollMore.hidden = !more || this.readMoreCompleted || Boolean(keyboard);
    });
  };

  resetReadMore() {
    this.clearReadMoreScroll();
    this.readMoreCompleted = false;
    this.scroller.scrollTop = 0;
    this.queueScrollCues();
  }

  clearReadMoreScroll() {
    clearTimeout(this.moreScrollTimer);
    this.moreScrollTimer = null;
    this.moreScrollPending = false;
    if (this.scrollMore) this.scrollMore.disabled = false;
  }

  finishReadMoreScroll = () => {
    this.clearReadMoreScroll();
    this.queueScrollCues();
  };

  onResultsScroll = () => {
    this.queueScrollCues();
    if (!this.moreScrollPending) return;
    // Fallback for browsers without scrollend, including interrupted motion.
    clearTimeout(this.moreScrollTimer);
    this.moreScrollTimer = setTimeout(this.finishReadMoreScroll, 160);
  };

  onScrollMore = (event) => {
    if (!this.dialog.open || this.dialog.dataset.state === 'closing' || this.scrollMore?.hidden ||
      this.readMoreCompleted || this.moreScrollPending || event?.detail > 1) return;
    const maximum = Math.max(0, this.scroller.scrollHeight - this.scroller.clientHeight);
    const current = Math.max(0, Math.min(this.scroller.scrollTop, maximum));
    const top = Math.min(maximum, current + Math.max(1, this.scroller.clientHeight - 96));
    if (top <= current) { this.queueScrollCues(); return; }
    // Pointer activation must not focus the region: Safari can scroll it again.
    if (event?.detail === 0) this.scroller.focus({ preventScroll: true });
    this.moreScrollPending = true;
    this.scrollMore.disabled = true;
    this.moreScrollTimer = setTimeout(this.finishReadMoreScroll, 500);
    this.scroller.scrollTo({ top, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  };

  stopResultsLimit = () => {
    this.clearReadMoreScroll();
    if (this.scrollMore) this.scrollMore.hidden = true;
    this.scroller?.removeEventListener('scroll', this.onResultsScroll);
    this.scroller?.removeEventListener('scrollend', this.finishReadMoreScroll);
    this.scrollObserver?.disconnect();
    if (this.cuesFrame) cancelAnimationFrame(this.cuesFrame);
    this.cuesFrame = null;
    this.scroller?.removeAttribute('data-search-scroll-start');
    this.scroller?.removeAttribute('data-search-scroll-end');
    this.viewport?.removeEventListener('resize', this.queueResultsLimit);
    this.viewport?.removeEventListener('scroll', this.queueResultsLimit);
    if (this.resultsFrame) cancelAnimationFrame(this.resultsFrame);
    this.resultsFrame = null;
    this.clearResultsLimit();
  };

  selectMode(mode, focus = false) {
    const selected = mode === 'questions' && this.host.querySelector('[data-search-question-input]') ? 'questions' : 'products';
    if (this.host.dataset.mode !== selected) {
      this.resetReadMore();
    }
    this.host.dataset.mode = selected;
    this.host.querySelectorAll('[data-search-panel]').forEach(panel => { panel.hidden = panel.dataset.searchPanel !== selected; });
    this.host.querySelectorAll('[data-search-mode]').forEach(tab => {
      const active = tab.dataset.searchMode === selected;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    const all = this.host.querySelector('[data-search-all]');
    if (all && selected === 'questions') all.hidden = true;
    if (focus) this.host.querySelector(selected === 'questions' ? '[data-search-question-input]' : '[data-search-input]')?.focus({ preventScroll: true });
    this.queueResultsLimit();
    return selected;
  }

  onKeydown = (event) => {
    if (event.key !== 'Escape' || event.defaultPrevented) return;
    event.preventDefault();
    window.AveyOverlayManager?.requestClose(this.dialog, { reason: 'escape' });
  };

  openGuide(opener = document.querySelector('.avey-header__search')) {
    const trigger = document.querySelector('avey-product button[data-avey-overlay-open^="shade-guide-"]');
    if (!trigger || !window.AveyOverlayManager) return false;
    window.AveyOverlayManager.open(trigger.dataset.aveyOverlayOpen, opener || trigger);
    return true;
  }

  openLinkedGuide = () => { if (window.location.hash === '#guide-des-teintes') this.openGuide(); };
}

if (!customElements.get('avey-search-loader')) customElements.define('avey-search-loader', AveySearchLoader);
